import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  ArrowLeftIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  UserIcon,
  AtSymbolIcon,
  PencilSquareIcon,
  ChatBubbleBottomCenterTextIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { 
  EnvelopeIcon as EnvelopeIconSolid,
  PhoneIcon as PhoneIconSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid
} from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { submitContactForm } from '../../services/contactFormService';

const ContactUsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    investorType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit to Google Sheets
      const result = await submitContactForm(formData);
      
      if (result.success) {
        toast.success(t('contact.successToast'), {
          duration: 5000,
          icon: '✅',
          style: {
            background: 'linear-gradient(135deg, #10b981, #059669)',
            color: 'white',
            fontWeight: '600',
            borderRadius: '16px',
            boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)'
          }
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          investorType: ''
        });
        setFocusedField(null);
      } else {
        throw new Error(result.error || t('contact.submitFailed'));
      }
    } catch (error) {
      console.error('Form submission error:', error);

      toast.error(error.message || t('contact.errorToast'), {
        duration: 5000,
        icon: '❌',
        style: {
          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
          color: 'white',
          fontWeight: '600',
          borderRadius: '16px',
          boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)'
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <EnvelopeIcon className="w-7 h-7" />,
      solidIcon: <EnvelopeIconSolid className="w-7 h-7" />,
      title: t('contact.emailUsTitle'),
      subtitle: t('contact.emailUsSubtitle'),
      details: ['info@honestlee.app'],
      color: '#3B82F6',
      bgGradient: 'linear-gradient(135deg, #3B82F6, #2563EB)',
      link: 'mailto:info@honestlee.app'
    },
    {
      icon: <PhoneIcon className="w-7 h-7" />,
      solidIcon: <PhoneIconSolid className="w-7 h-7" />,
      title: t('contact.callUsTitle'),
      subtitle: t('contact.callUsSubtitle'),
      details: ['+971 58 552 2677'],
      color: '#10B981',
      bgGradient: 'linear-gradient(135deg, #10B981, #059669)',
      link: 'tel:+971585522677'
    },
    {
      icon: <ChatBubbleLeftRightIcon className="w-7 h-7" />,
      solidIcon: <ChatBubbleLeftRightIconSolid className="w-7 h-7" />,
      title: 'WhatsApp',
      subtitle: t('contact.whatsappSubtitle'),
      details: ['+971 58 552 2677'],
      color: '#25D366',
      bgGradient: 'linear-gradient(135deg, #25D366, #128C7E)',
      isClickable: true,
      link: 'https://wa.me/971585522677',
      badge: t('contact.instantResponseBadge')
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #FEF3C7 0%, #FDE68A 50%, #FCD34D 100%)',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.8; transform: scale(1.05); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes shimmer {
            0% { background-position: -1000px 0; }
            100% { background-position: 1000px 0; }
          }
          @keyframes slideIn {
            from { transform: translateX(-100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          input, textarea {
            font-family: inherit;
          }
        `}
      </style>

      {/* Decorative Background Elements */}
      <div style={{
        position: 'absolute',
        top: '-100px',
        right: '-100px',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(251, 191, 36, 0.3) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-150px',
        left: '-150px',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(252, 211, 77, 0.3) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #184A99 0%, #1e40af 100%)',
        padding: '24px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: '0 8px 32px rgba(24, 74, 153, 0.3)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        overflow: 'hidden',
        backdropFilter: 'blur(10px)'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: `
            repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)
          `
        }} />
        
        <motion.button
          whileHover={{ scale: 1.1, rotate: -5 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '14px',
            padding: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            position: 'relative',
            zIndex: 1
          }}
        >
          <ArrowLeftIcon style={{ height: '22px', width: '22px', color: 'white' }} />
        </motion.button>
        
        <div style={{ flex: 1, position: 'relative', zIndex: 1 }}>
          <h1 style={{
            color: 'white',
            fontSize: '18px',
            fontWeight: '800',
            margin: '0 0 4px 0',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)',
            letterSpacing: '-0.5px'
          }}>
            {t('contact.pageTitle')}
          </h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '14px',
            fontWeight: '500',
            margin: 0
          }}>
            {t('contact.pageSubtitle')}
          </p>
        </div>
      </div>

      <div style={{ padding: '32px 20px', maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            textAlign: 'center',
            marginBottom: '48px'
          }}
        >
          <motion.div
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              display: 'inline-block',
              marginBottom: '16px'
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #184A99, #1e40af)',
              borderRadius: '24px',
              padding: '20px',
              display: 'inline-flex',
              boxShadow: '0 12px 40px rgba(24, 74, 153, 0.3)'
            }}>
              <ChatBubbleBottomCenterTextIcon style={{ width: '48px', height: '48px', color: 'white' }} />
            </div>
          </motion.div>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '900',
            background: 'linear-gradient(135deg, #184A99, #1e40af)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: '0 0 12px 0',
            letterSpacing: '-1px'
          }}>
            {t('contact.heroTitle')}
          </h2>
          <p style={{
            fontSize: '15px',
            color: '#6b7280',
            fontWeight: '500',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            {t('contact.heroSubtitle')}
          </p>
        </motion.div>

        {/* Contact Information Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginBottom: '48px'
          }}
        >
          {contactInfo.map((info, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              whileHover={{ y: -8, scale: 1.02 }}
              style={{
                backgroundColor: 'white',
                borderRadius: '24px',
                padding: '28px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                border: '2px solid rgba(255,255,255,0.8)',
                cursor: info.link ? 'pointer' : 'default',
                position: 'relative',
                overflow: 'hidden',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease'
              }}
              onClick={info.link ? () => window.open(info.link, '_blank') : undefined}
            >
              {/* Gradient Top Bar */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '5px',
                background: info.bgGradient,
                borderRadius: '24px 24px 0 0'
              }} />

              {/* Hover Glow Effect */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: info.bgGradient,
                opacity: 0,
                transition: 'opacity 0.3s ease',
                borderRadius: '24px',
                pointerEvents: 'none'
              }} className="hover-glow" />

              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    style={{
                      background: info.bgGradient,
                      borderRadius: '18px',
                      padding: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 8px 24px ${info.color}40`,
                      flexShrink: 0
                    }}
                  >
                    <div style={{ color: 'white' }}>
                      {info.solidIcon}
                    </div>
                  </motion.div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: '800',
                      color: '#1f2937',
                      margin: '0 0 4px 0',
                      letterSpacing: '-0.5px'
                    }}>
                      {info.title}
                    </h3>
                    <p style={{
                      fontSize: '13px',
                      color: '#9ca3af',
                      margin: 0,
                      fontWeight: '600'
                    }}>
                      {info.subtitle}
                    </p>
                  </div>
                </div>
                
                <div style={{ marginTop: '16px' }}>
                  {info.details.map((detail, idx) => (
                    <p key={idx} style={{
                      fontSize: '15px',
                      color: info.isClickable ? info.color : '#4b5563',
                      margin: '8px 0',
                      lineHeight: '1.5',
                      fontWeight: info.isClickable ? '700' : '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {info.isClickable && <span style={{ fontSize: '18px' }}>💬</span>}
                      {detail}
                    </p>
                  ))}
                  
                  {info.badge && (
                    <motion.div
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        marginTop: '14px',
                        padding: '10px 16px',
                        background: `${info.color}15`,
                        borderRadius: '12px',
                        border: `2px solid ${info.color}30`,
                        display: 'inline-block'
                      }}
                    >
                      <p style={{
                        fontSize: '13px',
                        color: info.color,
                        margin: 0,
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                        {info.badge}
                      </p>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          style={{
            backgroundColor: 'white',
            borderRadius: '32px',
            padding: '40px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
            border: '2px solid rgba(255,255,255,0.9)',
            position: 'relative',
            overflow: 'hidden',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Form Header Gradient */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #184A99, #1e40af, #3B82F6)',
            borderRadius: '32px 32px 0 0'
          }} />
          
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{
                display: 'inline-block',
                marginBottom: '16px'
              }}
            >
              <div style={{
                background: 'linear-gradient(135deg, rgba(24, 74, 153, 0.1), rgba(30, 64, 175, 0.1))',
                borderRadius: '20px',
                padding: '16px',
                display: 'inline-flex'
              }}>
                <PaperAirplaneIcon style={{ width: '40px', height: '40px', color: '#184A99' }} />
              </div>
            </motion.div>
            <h2 style={{
              fontSize: '32px',
              fontWeight: '900',
              color: '#1f2937',
              margin: '0 0 12px 0',
              letterSpacing: '-1px'
            }}>
              {t('contact.formTitle')}
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              margin: 0,
              fontWeight: '500',
              lineHeight: '1.6'
            }}>
              {t('contact.formSubtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              marginBottom: '24px'
            }}>
              {/* Name Field */}
              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '15px',
                  fontWeight: '700',
                  color: '#374151',
                  marginBottom: '10px'
                }}>
                  <UserIcon style={{ width: '18px', height: '18px', color: '#184A99' }} />
                  {t('contact.nameLabel')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder={t('contact.namePlaceholder')}
                    style={{
                      width: '100%',
                      padding: '18px 20px',
                      paddingLeft: '48px',
                      border: focusedField === 'name' ? '3px solid #184A99' : '2px solid #e5e7eb',
                      borderRadius: '16px',
                      fontSize: '15px',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                      backgroundColor: focusedField === 'name' ? 'white' : '#fafafa',
                      boxShadow: focusedField === 'name' ? '0 0 0 4px rgba(24, 74, 153, 0.1)' : 'none',
                      fontWeight: '500'
                    }}
                  />
                  <UserIcon style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '20px',
                    height: '20px',
                    color: focusedField === 'name' ? '#184A99' : '#9ca3af',
                    transition: 'color 0.3s ease'
                  }} />
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '15px',
                  fontWeight: '700',
                  color: '#374151',
                  marginBottom: '10px'
                }}>
                  <AtSymbolIcon style={{ width: '18px', height: '18px', color: '#184A99' }} />
                  {t('contact.emailLabel')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder="your.email@example.com"
                    style={{
                      width: '100%',
                      padding: '18px 20px',
                      paddingLeft: '48px',
                      border: focusedField === 'email' ? '3px solid #184A99' : '2px solid #e5e7eb',
                      borderRadius: '16px',
                      fontSize: '15px',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                      backgroundColor: focusedField === 'email' ? 'white' : '#fafafa',
                      boxShadow: focusedField === 'email' ? '0 0 0 4px rgba(24, 74, 153, 0.1)' : 'none',
                      fontWeight: '500'
                    }}
                  />
                  <AtSymbolIcon style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '20px',
                    height: '20px',
                    color: focusedField === 'email' ? '#184A99' : '#9ca3af',
                    transition: 'color 0.3s ease'
                  }} />
                </div>
              </div>
            </div>

            {/* Subject Field */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '15px',
                fontWeight: '700',
                color: '#374151',
                marginBottom: '10px'
              }}>
                <PencilSquareIcon style={{ width: '18px', height: '18px', color: '#184A99' }} />
                {t('contact.subjectLabel')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('subject')}
                  onBlur={() => setFocusedField(null)}
                  required
                  placeholder={t('contact.subjectPlaceholder')}
                  style={{
                    width: '100%',
                    padding: '18px 20px',
                    paddingLeft: '48px',
                    border: focusedField === 'subject' ? '3px solid #184A99' : '2px solid #e5e7eb',
                    borderRadius: '16px',
                    fontSize: '15px',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    backgroundColor: focusedField === 'subject' ? 'white' : '#fafafa',
                    boxShadow: focusedField === 'subject' ? '0 0 0 4px rgba(24, 74, 153, 0.1)' : 'none',
                    fontWeight: '500'
                  }}
                />
                <PencilSquareIcon style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '20px',
                  height: '20px',
                  color: focusedField === 'subject' ? '#184A99' : '#9ca3af',
                  transition: 'color 0.3s ease'
                }} />
              </div>
            </div>

            {/* Investor Type Field */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '15px',
                fontWeight: '700',
                color: '#374151',
                marginBottom: '10px'
              }}>
                <BuildingOfficeIcon style={{ width: '18px', height: '18px', color: '#184A99' }} />
                {t('contact.investorTypeLabel')}
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  name="investorType"
                  value={formData.investorType}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('investorType')}
                  onBlur={() => setFocusedField(null)}
                  required
                  style={{
                    width: '100%',
                    padding: '18px 20px',
                    paddingLeft: '48px',
                    border: focusedField === 'investorType' ? '3px solid #184A99' : '2px solid #e5e7eb',
                    borderRadius: '16px',
                    fontSize: '15px',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    backgroundColor: focusedField === 'investorType' ? 'white' : '#fafafa',
                    boxShadow: focusedField === 'investorType' ? '0 0 0 4px rgba(24, 74, 153, 0.1)' : 'none',
                    fontWeight: '500',
                    appearance: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">{t('contact.selectVisitorType')}</option>
                  <option value="Individual Investor">{t('contact.investorIndividual')}</option>
                  <option value="Venture Capital">{t('contact.investorVC')}</option>
                  <option value="Angel Investor">{t('contact.investorAngel')}</option>
                  <option value="Institutional Investor">{t('contact.investorInstitutional')}</option>
                  <option value="Private Equity">{t('contact.investorPE')}</option>
                  <option value="Family Office">{t('contact.investorFamilyOffice')}</option>
                  <option value="Corporate Investor">{t('contact.investorCorporate')}</option>
                  <option value="vistor">{t('contact.investorVisitor')}</option>
                </select>
                <BuildingOfficeIcon style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '20px',
                  height: '20px',
                  color: focusedField === 'investorType' ? '#184A99' : '#9ca3af',
                  transition: 'color 0.3s ease',
                  pointerEvents: 'none'
                }} />
                {/* Custom dropdown arrow */}
                <svg 
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '20px',
                    height: '20px',
                    color: '#9ca3af',
                    pointerEvents: 'none'
                  }} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  strokeWidth="1.5" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                </svg>
              </div>
            </div>

            {/* Message Field */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '15px',
                fontWeight: '700',
                color: '#374151',
                marginBottom: '10px'
              }}>
                <ChatBubbleBottomCenterTextIcon style={{ width: '18px', height: '18px', color: '#184A99' }} />
                {t('contact.messageLabel')}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                onFocus={() => setFocusedField('message')}
                onBlur={() => setFocusedField(null)}
                required
                rows={6}
                placeholder={t('contact.messagePlaceholder')}
                style={{
                  width: '100%',
                  padding: '18px 20px',
                  border: focusedField === 'message' ? '3px solid #184A99' : '2px solid #e5e7eb',
                  borderRadius: '16px',
                  fontSize: '15px',
                  transition: 'all 0.3s ease',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  backgroundColor: focusedField === 'message' ? 'white' : '#fafafa',
                  lineHeight: '1.6',
                  boxShadow: focusedField === 'message' ? '0 0 0 4px rgba(24, 74, 153, 0.1)' : 'none',
                  fontWeight: '500',
                  minHeight: '150px'
                }}
              />
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.02, y: isSubmitting ? 0 : -2 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              style={{
                width: '100%',
                background: isSubmitting 
                  ? 'linear-gradient(135deg, #9ca3af, #6b7280)' 
                  : 'linear-gradient(135deg, #184A99, #1e40af)',
                color: 'white',
                border: 'none',
                borderRadius: '18px',
                padding: '20px 24px',
                fontSize: '17px',
                fontWeight: '800',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                transition: 'all 0.3s ease',
                boxShadow: isSubmitting 
                  ? '0 4px 12px rgba(156, 163, 175, 0.3)' 
                  : '0 12px 32px rgba(24, 74, 153, 0.4)',
                letterSpacing: '0.5px',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Button shimmer effect */}
              {!isSubmitting && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: 'shimmer 2s infinite'
                }} />
              )}
              {isSubmitting ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    style={{
                      width: '22px',
                      height: '22px',
                      border: '3px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '3px solid white',
                      borderRadius: '50%'
                    }}
                  />
                  {t('contact.sendingMessage')}
                </>
              ) : (
                <>
                  <PaperAirplaneIcon style={{ height: '22px', width: '22px' }} />
                  {t('contact.sendMessage')}
                </>
              )}
            </motion.button>
          </form>
        </motion.div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          style={{
            marginTop: '40px',
            padding: '32px',
            background: 'linear-gradient(135deg, rgba(24, 74, 153, 0.08), rgba(30, 64, 175, 0.12))',
            borderRadius: '24px',
            border: '2px solid rgba(24, 74, 153, 0.15)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '12px'
          }}>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#10b981',
                borderRadius: '50%',
                boxShadow: '0 0 20px #10b981'
              }}
            />
            <p style={{
              fontSize: '18px',
              color: '#1f2937',
              margin: 0,
              fontWeight: '800',
              letterSpacing: '-0.5px'
            }}>
              {t('contact.footerTitle')}
            </p>
          </div>
          <p style={{
            fontSize: '15px',
            color: '#4b5563',
            margin: 0,
            lineHeight: '1.8',
            textAlign: 'center',
            fontWeight: '500'
          }}>
            {t('contact.footerBodyPrefix')} <strong style={{ color: '#184A99' }}>{t('contact.footerBody24h')}</strong>{t('contact.footerBodyMiddle')} <strong style={{ color: '#25D366' }}>WhatsApp</strong> {t('contact.footerBodySuffix')}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactUsPage;
